import { api } from "./api"

/** Papéis e tipos como a API manda (números, ADR-021). */
export const WalletRole = { Owner: 1, Editor: 2, Viewer: 3 } as const
export type WalletRole = (typeof WalletRole)[keyof typeof WalletRole]
export const WalletType = { Personal: 1, Shared: 2 } as const
export type WalletType = (typeof WalletType)[keyof typeof WalletType]

export const ROLE_LABEL: Record<WalletRole, string> = { 1: "Titular", 2: "Editor", 3: "Leitor" }

export interface Wallet {
  id: string
  name: string
  type: WalletType
  myRole: WalletRole
  memberCount: number
  isActive: boolean
  /** Falso para leitor ou conjunta sem plano de grupo do titular (somente leitura). */
  canWrite: boolean
}

export interface WalletMember {
  userId: string
  name: string
  email: string
  avatarUrl?: string | null
  role: WalletRole
  joinedAt: string
  isCurrentUser: boolean
}

export interface WalletInvite {
  id: string
  email: string
  role: WalletRole
  expiresAt: string
  createdAt: string
}

export interface WalletDetail {
  id: string
  name: string
  type: WalletType
  myRole: WalletRole
  isActive: boolean
  members: WalletMember[]
  /** Só o titular recebe os convites pendentes. */
  pendingInvites: WalletInvite[]
  maxMembers: number
  canWrite: boolean
  /** Aviso a cada lançamento na conjunta, de quem está vendo (ADR-021, F5). */
  notifyOnActivity?: boolean
}

export interface InvitePreview {
  walletName: string
  inviterName: string
  role: WalletRole
  memberCount: number
  expiresAt: string
}

/** Conta ou cartão da carteira pessoal e se está visível para o grupo (ADR-021, F4). */
export interface SharedItem {
  id: string
  name: string
  bankName?: string | null
  sharedWithGroup: boolean
}

export interface SharingChoices {
  accounts: SharedItem[]
  cards: SharedItem[]
}

export interface SharingSelection {
  accountIds: string[]
  cardIds: string[]
}

/** Seleção inicial: o que já está visível para o grupo. */
export function sharedSelection(choices: SharingChoices): SharingSelection {
  return {
    accountIds: choices.accounts.filter((a) => a.sharedWithGroup).map((a) => a.id),
    cardIds: choices.cards.filter((c) => c.sharedWithGroup).map((c) => c.id),
  }
}

/** Onde o item está na visão conjunta (ADR-021, F4). */
export const JointScope = { Joint: 1, Personal: 2 } as const
export type JointScope = (typeof JointScope)[keyof typeof JointScope]

interface JointOwned {
  id: string
  name: string
  bankName?: string | null
  color: string
  scope: JointScope
  ownerUserId: string
  ownerName: string
  isMine: boolean
}
export type JointAccount = JointOwned & { balance: number }
export type JointCard = JointOwned

export interface JointSummary {
  walletId: string
  walletName: string
  year: number
  month: number
  totalBalance: number
  income: number
  expenses: number
  accounts: JointAccount[]
  cards: JointCard[]
  people: { userId: string; name: string; isMe: boolean; income: number; expenses: number }[]
  categories: { name: string; color: string; icon?: string | null; amount: number }[]
}

export interface JointTransaction {
  id: string
  date: string
  description: string
  amount: number
  /** 1 receita, 2 despesa, 3 transferência */
  type: 1 | 2 | 3
  categoryName?: string | null
  categoryColor?: string | null
  categoryIcon?: string | null
  bankAccountId?: string | null
  accountName?: string | null
  creditCardId?: string | null
  cardName?: string | null
  personId: string
  personName: string
  scope: JointScope
  isMine: boolean
}

export interface JointTransactionsPage {
  items: JointTransaction[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

export interface JointTransactionsFilter {
  year: number
  month: number
  personId?: string
  accountId?: string
  cardId?: string
  page?: number
  pageSize?: number
}

/** Item do feed da carteira conjunta (ADR-021, F5). */
export interface ActivityItem {
  id: string
  action: number
  actorName: string
  isMe: boolean
  description: string
  /** Nas ações sobre pessoas (remover, mudar papel): quem foi o alvo. */
  targetName?: string | null
  createdAt: string
}

export interface ActivityPage {
  items: ActivityItem[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

export const walletService = {
  getAll: async (): Promise<Wallet[]> => (await api.get("/wallets")).data,
  getDetail: async (id: string): Promise<WalletDetail> => (await api.get(`/wallets/${id}`)).data,
  create: async (name: string): Promise<Wallet> => (await api.post("/wallets", { name })).data,
  activate: async (id: string): Promise<void> => { await api.patch(`/wallets/${id}/activate`) },
  remove: async (id: string): Promise<void> => { await api.delete(`/wallets/${id}`) },

  invite: async (id: string, email: string, role: WalletRole): Promise<void> => {
    await api.post(`/wallets/${id}/invites`, { email, role })
  },
  cancelInvite: async (id: string, inviteId: string): Promise<void> => { await api.delete(`/wallets/${id}/invites/${inviteId}`) },
  updateRole: async (id: string, userId: string, role: WalletRole): Promise<void> => {
    await api.patch(`/wallets/${id}/members/${userId}`, { role })
  },
  /** Titular remove alguém; com o próprio id, é a saída do grupo. */
  removeMember: async (id: string, userId: string): Promise<void> => { await api.delete(`/wallets/${id}/members/${userId}`) },

  // O token vai no corpo, nunca na URL (fica fora dos logs)
  previewInvite: async (token: string): Promise<InvitePreview> => (await api.post("/wallets/invite-preview", { token })).data,
  acceptInvite: async (token: string, visible: SharingSelection): Promise<Wallet> =>
    (await api.post("/wallets/accept-invite", {
      token, consent: true, visibleAccountIds: visible.accountIds, visibleCardIds: visible.cardIds,
    })).data,

  getJointSummary: async (year: number, month: number): Promise<JointSummary> =>
    (await api.get("/wallets/joint-view/summary", { params: { year, month } })).data,
  getJointTransactions: async (filter: JointTransactionsFilter): Promise<JointTransactionsPage> =>
    (await api.get("/wallets/joint-view/transactions", { params: filter })).data,

  setGroupNotifications: async (notifyOnActivity: boolean): Promise<void> => {
    await api.put("/wallets/notifications", { notifyOnActivity })
  },
  getActivity: async (page: number, pageSize = 30): Promise<ActivityPage> =>
    (await api.get("/wallets/activity", { params: { page, pageSize } })).data,

  getSharing: async (): Promise<SharingChoices> => (await api.get("/wallets/sharing")).data,
  updateSharing: async (selection: SharingSelection): Promise<SharingChoices> =>
    (await api.put("/wallets/sharing", selection)).data,
}
